import type { GradeItemAction } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/types/AssignmentGraderModule';
import type { GradeComparatorRow } from 'bundles/author-assignment-grading/components/common/components/ItemSubmissionsTableV2';
import type Team from 'bundles/author-assignment-grading/components/common/types/Team';
import type { LtiGradingState } from 'bundles/author-assignment-grading/constants/LtiGradingStates';
import {
  LtiGradingStates,
  getLtiGradingStateTranslations,
} from 'bundles/author-assignment-grading/constants/LtiGradingStates';
import type { SgaGradingState } from 'bundles/author-assignment-grading/constants/SgaGradingStates';
import {
  SgaGradingStates,
  getSgaGradingStateTranslations,
} from 'bundles/author-assignment-grading/constants/SgaGradingStates';
import { compareByNaturalSortOrder } from 'bundles/author-common/utils/common';
import type { ItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import WorkspacesExperiments from 'bundles/epic/clients/Workspaces';
import type { UserItemAction } from 'bundles/naptimejs/resources/__generated__/AuditTrailHistoryV1';

export const NON_EXTRA_CREDIT_MAX_SCORE = 100;

export const EXTRA_CREDIT_MAX_SCORE = 1000;

export const EXTRA_CREDIT_RUBRIC_POINTS_MULTIPLIER = 10;

/**
 * The main goal is that Partners who are currently using IRT, should not see this feature enabled, and continue to see what they currently see.
 * The partner who are not using IRT and do not have the authoringEnabled epic set for them, we don't need to worry about them since they won't be able to create one in the first place, but at the same time, anyone with the IRI_Enabled epic should be able to see all the new features.
 * If anyone who does not have authoringEnabled epic setup, will not be able to create an IRI or IRT in the first place based on the condition on line static/bundles/author-team/components/ProjectOptionEditor.tsx:526
 * Happy to talk in person to explain this further if this is confusing, or you find any concerns with it.
 * Reach out to skohli
 *
 * The original long-running experiment "[UoL Phase 2] IRT authoringEnabled"
 * will be ended as part of the Epic Cleanup OKR. In case we need to re-enable
 * the feature, hard-code the original values in IRT_COURSE_IDS or make a copy
 * of the original epic.
 * https://tools.coursera.org/epic/experiment/kl-T4IvwEemC3Kv3M-kuwQ
 * https://coursera.atlassian.net/browse/GRADE-6156
 */
const IRT_COURSE_IDS: string[] = [];
export const shouldUseIRTEpic = (courseId: string) => IRT_COURSE_IDS.includes(courseId);

/**
 * Comparator for SGA submissions table grading status
 */
export const sgaSubmissionsTableGradingStatusComparator = (
  firstRow: GradeComparatorRow,
  secondRow: GradeComparatorRow
) => {
  const firstGradeStatus =
    firstRow?.props?.isGradeReleased && firstRow?.props?.gradingStatus === SgaGradingStates.GRADING_STARTED
      ? SgaGradingStates.GRADING_FINISHED
      : (firstRow?.props?.gradingStatus as SgaGradingState);

  const secondGradeStatus =
    secondRow?.props?.isGradeReleased && secondRow?.props?.gradingStatus === SgaGradingStates.GRADING_STARTED
      ? SgaGradingStates.GRADING_FINISHED
      : (secondRow?.props?.gradingStatus as SgaGradingState);

  return compareByNaturalSortOrder(
    getSgaGradingStateTranslations(firstGradeStatus),
    getSgaGradingStateTranslations(secondGradeStatus)
  );
};

/**
 * Comparator for LTI submissions table grading status
 */
export const ltiSubmissionsTableGradingStatusComparator = (
  firstRow: GradeComparatorRow,
  secondRow: GradeComparatorRow
) => {
  const firstGradeStatus =
    firstRow?.props?.gradingStatus === LtiGradingStates.GRADING_STARTED
      ? LtiGradingStates.GRADING_FINISHED
      : (firstRow?.props?.gradingStatus as LtiGradingState);

  const secondGradeStatus =
    secondRow?.props?.gradingStatus === LtiGradingStates.GRADING_STARTED
      ? LtiGradingStates.GRADING_FINISHED
      : (secondRow?.props?.gradingStatus as LtiGradingState);

  return compareByNaturalSortOrder(
    getLtiGradingStateTranslations(firstGradeStatus),
    getLtiGradingStateTranslations(secondGradeStatus)
  );
};

/**
 * Get latest grader action item for Grading page change log
 */
export const getLatestGradeItemAction = (gradeItemActions: (GradeItemAction | null | undefined)[]): GradeItemAction => {
  const sortedItemActions = (gradeItemActions.filter(Boolean) as GradeItemAction[]).sort(
    (action1, action2) => (action2?.actionTime || 0) - (action1?.actionTime || 0)
  );

  return sortedItemActions[0];
};

/**
 * Filter Grader Item Actions Logs from Audit Trail
 */
export const filterGradeItemActionsLogsFromAuditTrail = (logs: UserItemAction[], gradingChangeItemTypes: string[]) => {
  return logs.filter((log) => gradingChangeItemTypes.includes(log.typeName));
};

/**
 * Checks whether all team members in given team are within the same
 * grading group
 *
 * @param { Team } team
 * @param {{ [userId: string]: ItemGrade }} grades // TODO: Create a dedicated type for legacy grades object
 * @returns boolean
 */
export const allTeamMembersAreInGroup = (team: Team, grades: { [userId: string]: ItemGrade }): boolean => {
  return team.members.filter((member) => !grades[member]).length === 0;
};

// https://tools.coursera.org/epic/experiment/Zgo4Gkg7Ee6YYwrfq_--AQ
export const isInstructorLabLaunchEnabledForCourse = (courseId: string): boolean => {
  const map: Record<string, boolean> = WorkspacesExperiments.get('enabledCourseIdsForInstructorLaunchLab') || {};
  const isCourseDisabled = map[courseId] === false;
  const isCourseEnabled = map[courseId] === true;
  const isRolledOut = map['*'] === true;

  return !isCourseDisabled && (isCourseEnabled || isRolledOut);
};
